import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/UI/Toast';
import ErrorBoundary from './components/UI/ErrorBoundary';
import Navbar from './components/Navbar/Navbar';
import AIAssistant from './components/AI/AIAssistant';
import BackToTop from './components/UI/BackToTop';
import './App.css';

// Retry wrapper for lazy imports — handles stale chunks after redeployment
function lazyRetry(importFn) {
  return lazy(() =>
    importFn().catch(() => {
      // If chunk fails to load (stale deployment), force a full page reload once
      const hasReloaded = sessionStorage.getItem('chunk_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      sessionStorage.removeItem('chunk_reload');
      // If we already reloaded once, throw so ErrorBoundary catches it
      return importFn();
    })
  );
}

// Lazy-loaded pages for code splitting
const Login = lazyRetry(() => import('./pages/Login'));
const Register = lazyRetry(() => import('./pages/Register'));
const Home = lazyRetry(() => import('./pages/Home'));
const Profile = lazyRetry(() => import('./pages/Profile'));
const Network = lazyRetry(() => import('./pages/Network'));
const Messaging = lazyRetry(() => import('./pages/Messaging'));
const Notifications = lazyRetry(() => import('./pages/Notifications'));
const Communities = lazyRetry(() => import('./pages/Communities'));
const CommunityDetail = lazyRetry(() => import('./pages/CommunityDetail'));
const NotFound = lazyRetry(() => import('./pages/NotFound'));
const AuthCallback = lazyRetry(() => import('./pages/AuthCallback'));
const ForgotPassword = lazyRetry(() => import('./pages/ForgotPassword'));
const SavedPosts = lazyRetry(() => import('./pages/SavedPosts'));
const Landing = lazyRetry(() => import('./pages/Landing'));

function PageLoader() {
  return (
    <div className="app-loading">
      <div className="page-loader">
        <div className="spinner" />
        <p className="page-loader-text">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  return user ? children : <Navigate to="/welcome" />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  return !user ? children : <Navigate to="/home" />;
}

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return (
    <>
      {user && <Navbar />}
      <div className={user ? 'app-container' : ''}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/welcome" element={<PublicRoute><Landing /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/profile/:id" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/network" element={<ProtectedRoute><Network /></ProtectedRoute>} />
            <Route path="/messaging" element={<ProtectedRoute><Messaging /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/communities" element={<ProtectedRoute><Communities /></ProtectedRoute>} />
            <Route path="/communities/:id" element={<ProtectedRoute><CommunityDetail /></ProtectedRoute>} />
            <Route path="/saved" element={<ProtectedRoute><SavedPosts /></ProtectedRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
      {user && <AIAssistant />}
      {user && <BackToTop />}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <Router>
            <AppContent />
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
